import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useBoolean } from 'react-use'

import { IACL, OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'

import { useACLQuery } from '@/resolvers/org/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useOrgPerm } from '@/hooks/useOrgPerm'

/**
 * Custom hook to manage Access Control List (ACL) details.
 * It provides the state and functions necessary to fetch and update ACL,
 * as well as the permissions to make changes or view the ACL.
 *
 * @returns {object} Various state variables and functions
 *    @property {boolean} isLoading - Whether the ACL data is currently being loaded.
 *    @property {boolean} canEither - Whether the current user can view or make changes to the ACL.
 *    @property {IACL} rules - The ACL rules.
 *    @property {object} location - The current location object from the router.
 *    @property {object} currentOrg - The currently selected organization.
 *    @property {boolean} isSaved - Whether the changes to the ACL were saved.
 *    @property {function} setIsSaved - Function to set the isSaved state.
 */
const useDetailACL = () => {
  const location = useLocation()
  const { currentOrg } = useCurrentOrgContext()

  // TODO: checking backend acl
  const { data: rules, isLoading } = useACLQuery()

  // Check if the current user has the permission to make changes to the ACL
  const canMakeChange = useOrgPerm(OrgPermission.ACLMakeChanges)

  // Check if the current user has the permission to view the ACL
  const canViewACL = useOrgPerm(OrgPermission.ACLView)

  // If the user has either of the two permissions, set `canEither` to true
  const canEither = canMakeChange || canViewACL

  // State variable to track if the changes to the ACL were saved
  const [isSaved, setSaved] = useBoolean(false)

  // Callback function to set `isSaved` to true and then automatically set it back to false after 1.2 seconds
  const setIsSaved = useCallback(
    (newSaved: boolean) => {
      if (newSaved) {
        setSaved(newSaved)
        return new Promise(() => setTimeout(() => setSaved(false), 1200))
      }
    },
    [setSaved] // Dependency for the useCallback hook
  )

  return {
    isLoading,
    canEither,
    rules,
    location,
    currentOrg,
    isSaved,
    setIsSaved,
  }
}

export default useDetailACL
